import {
  useGetTeamMessagesQuery,
  useSendTeamMessageMutation,
} from 'features/TeamChat/api/teamChat.api'
import { lastConfirmedId, pendingMessage } from 'features/TeamChat/api/teamChat.pending'
import { teamErrorText } from 'features/TeamChat/api/teamChat.transform'
import type { TeamMember, TeamUser } from 'features/TeamChat/api/teamChat.types'
import { useMarkRead } from 'features/TeamChat/hooks/useMarkRead'
import { TEAM_COPY } from 'features/TeamChat/teamChat.constants'
import styles from 'features/TeamChat/teamChat.module.scss'

import { ChatComposer } from './ChatComposer'
import type { SendResult } from './ChatComposer'
import { MessageList } from './MessageList'
import type { MessagesStatus } from './MessageList'

type Props = { members: TeamMember[]; timeZone: string }

const UNKNOWN_ME: TeamUser = { id: 0, name: '', handle: '', role: '', color: '', initial: '' }

// Mounted only while the panel is open on the Chat tab, so whatever it shows is being read.
export const ChatTab = ({ members, timeZone }: Props) => {
  const { data, isError } = useGetTeamMessagesQuery()
  const [sendMessage] = useSendTeamMessageMutation()
  const messages = data?.messages ?? []
  const status: MessagesStatus = data ? 'ready' : isError ? 'error' : 'loading'
  useMarkRead({ lastId: lastConfirmedId(messages), lastReadId: data?.last_read_message_id })
  const me = members.find((m) => m.is_me)

  // The send time is a real instant; it is shown in the site zone like every other message.
  const onSend = (body: string, createRequest: boolean): Promise<SendResult> =>
    sendMessage({
      body,
      create_request: createRequest || undefined,
      pending: pendingMessage(body, me ?? UNKNOWN_ME, new Date().toISOString()),
    })
      .unwrap()
      .then(() => ({ ok: true }))
      .catch((e: unknown) => ({ ok: false, error: teamErrorText(e, TEAM_COPY.SEND_FAILED) }))

  return (
    <div className={styles.body}>
      <MessageList
        messages={messages}
        status={status}
        hasMore={Boolean(data?.has_more)}
        members={members}
        timeZone={timeZone}
      />
      <ChatComposer members={members} onSend={onSend} />
    </div>
  )
}
